import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  ArrowLeft,
  Save,
  CheckCircle,
  Lock,
  ClipboardList,
  PlayCircle,
  Eye,
  RefreshCw,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Library,
  AlertTriangle,
  ExternalLink,
  Download,
} from "lucide-react";
import toast from "react-hot-toast";
import { apiService, extractApiError } from "../services/api";
import { useAuth } from "../hooks/useAuth";
import CRAComplianceQuestionCard from "../components/CRAComplianceQuestion";
import CRAComplianceFeedback from "../components/CRAComplianceFeedback";
import EconomicOperatorsCard from "../components/EconomicOperatorsCard";
import {
  CRAComplianceQuestion,
  CRAComplianceAnswer,
  CRAEconomicOperator,
  isAdmin,
} from "../types";

interface AnswerState {
  answer: string; // "Yes" | "No" | "Not applicable" | ""
  details: string;
}

export default function CRAComplianceAssessmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const loadedAssessmentRef = useRef<string | null>(null);
  const landingScrollRef = useRef<HTMLDivElement | null>(null);

  const [existingAssessmentId, setExistingAssessmentId] = useState<
    string | null
  >(null);
  const [assessmentStarted, setAssessmentStarted] = useState(false);
  const [answers, setAnswers] = useState<Record<string, AnswerState>>({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // ── Queries ─────────────────────────────────────────────────────────
  const { data: productData, isLoading: productLoading } = useQuery(
    ["product", id],
    () => apiService.getProduct(id!),
    {
      enabled: !!id,
    },
  );

  const { data: questionsData } = useQuery(
    "craComplianceQuestions",
    apiService.getCRAComplianceQuestions,
    {
      enabled: !!id,
      staleTime: 5 * 60 * 1000,
    },
  );

  const {
    data: existingAssessmentData,
    isError: assessmentNotFound,
    isLoading: assessmentLoading,
    isFetching: assessmentFetching,
  } = useQuery(
    ["craComplianceAssessment", id],
    () => apiService.getLatestCRAComplianceAssessment(id!),
    {
      enabled: !!id,
      retry: false,
      cacheTime: 0,
    },
  );

  // Economic operators are surfaced only in connection with Question 1, but we
  // fetch them here so the Q1 conditional guidance has the data ready. Shares
  // the same query key as the embedded EconomicOperatorsCard for cache reuse.
  const { data: economicOperatorsData } = useQuery(
    ["economic-operators", id],
    () => apiService.listCRAEconomicOperators(id!),
    { enabled: !!id },
  );
  const economicOperators: CRAEconomicOperator[] =
    (economicOperatorsData?.data as any)?.data?.items ?? [];

  const product = productData?.data?.data;
  const apiQuestions: CRAComplianceQuestion[] =
    (questionsData?.data as any)?.data?.questions || [];

  // Flatten the list of all answerable refs for progress tracking
  const allAnswerableRefs = useMemo(() => {
    const refs: string[] = [];
    apiQuestions.forEach((q) => {
      if (q.subQuestions.length === 0) {
        refs.push(q.ref);
      } else {
        q.subQuestions.forEach((sq) => refs.push(sq.ref));
      }
    });
    return refs;
  }, [apiQuestions]);

  const existingAssessment = assessmentNotFound
    ? null
    : (existingAssessmentData?.data as any)?.data;
  const isViewOnly = existingAssessment?.status === "submitted";
  const canEdit = !isViewOnly;

  // Effective CRA class derived from either the manually-set classification or
  // the CRA Applicability Assessment result. Used to gate "Begin Assessment".
  const craClass = deriveCraClass(
    product?.craClassification,
    product?.craApplicability,
  );

  // ── Mutations ───────────────────────────────────────────────────────
  const saveMutation = useMutation(
    (payload: any) => {
      if (existingAssessmentId) {
        return apiService.updateCRAComplianceAssessment(
          existingAssessmentId,
          payload,
        );
      }
      return apiService.createCRAComplianceAssessment(id!, payload);
    },
    {
      onSuccess: (response: any) => {
        const newId = response?.data?.data?.id;
        if (newId && !existingAssessmentId) {
          setExistingAssessmentId(newId);
          loadedAssessmentRef.current = newId;
        }
        queryClient.invalidateQueries(["craComplianceAssessment", id]);
        queryClient.invalidateQueries(["product", id]);
        toast.success("Assessment draft saved");
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error || "Failed to save assessment");
      },
    },
  );

  const submitMutation = useMutation(
    (assessmentId: string) =>
      apiService.submitCRAComplianceAssessment(assessmentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["craComplianceAssessment", id]);
        queryClient.invalidateQueries(["product", id]);
        setShowFeedback(true);
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to submit assessment",
        );
      },
    },
  );

  const retakeMutation = useMutation(
    (assessmentId: string) =>
      apiService.retakeCRAComplianceAssessment(assessmentId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["craComplianceAssessment", id]);
        queryClient.invalidateQueries(["product", id]);
        loadedAssessmentRef.current = null;
        setAssessmentStarted(true);
        toast.success("Assessment reopened for editing");
      },
      onError: (error: any) => {
        toast.error(
          error.response?.data?.error || "Failed to retake assessment",
        );
      },
    },
  );

  // ── Effects ─────────────────────────────────────────────────────────

  // When the user is on the initial landing screen, ensure the scroll container
  // is reset to top so the hero/card area is never shown partially clipped.
  useEffect(() => {
    const onLanding = !assessmentStarted && !existingAssessment;
    if (onLanding && landingScrollRef.current) {
      landingScrollRef.current.scrollTop = 0;
    }
  }, [assessmentStarted, existingAssessmentId, existingAssessment]);

  // Redirect non-owners
  useEffect(() => {
    if (
      !productLoading &&
      product &&
      user &&
      product.ownerId !== user.id &&
      !isAdmin(user)
    ) {
      toast.error("Only the product owner or an admin can take assessments");
      navigate(`/products/${id}`);
    }
  }, [product, user, navigate, productLoading, id]);

  // Initialize blank answers when questions load
  useEffect(() => {
    if (allAnswerableRefs.length === 0) return;
    setAnswers((prev) => {
      if (Object.keys(prev).length > 0) return prev;
      const initial: Record<string, AnswerState> = {};
      allAnswerableRefs.forEach((ref) => {
        initial[ref] = { answer: "", details: "" };
      });
      return initial;
    });
  }, [allAnswerableRefs]);

  // Load existing assessment answers
  useEffect(() => {
    if (assessmentLoading || assessmentFetching) return;

    if (!existingAssessment) {
      setExistingAssessmentId(null);
      loadedAssessmentRef.current = null;
      if (allAnswerableRefs.length > 0) {
        const cleared: Record<string, AnswerState> = {};
        allAnswerableRefs.forEach((ref) => {
          cleared[ref] = { answer: "", details: "" };
        });
        setAnswers(cleared);
      }
      return;
    }

    const assessmentId = existingAssessment.id || null;
    setExistingAssessmentId(assessmentId);

    const isSubmitted = existingAssessment.status === "submitted";
    if (!isSubmitted && !assessmentStarted) return;

    if (loadedAssessmentRef.current === assessmentId) return;
    loadedAssessmentRef.current = assessmentId;

    let parsedAnswers: CRAComplianceAnswer[] = [];
    if (Array.isArray(existingAssessment.answers)) {
      parsedAnswers = existingAssessment.answers;
    } else if (typeof existingAssessment.answers === "string") {
      try {
        parsedAnswers = JSON.parse(existingAssessment.answers);
      } catch {
        parsedAnswers = [];
      }
    }

    const nextAnswers: Record<string, AnswerState> = {};
    allAnswerableRefs.forEach((ref) => {
      const found = parsedAnswers.find((a) => a.questionRef === ref);
      nextAnswers[ref] = {
        answer: found?.answer || "",
        details: found?.details || "",
      };
    });
    setAnswers(nextAnswers);
  }, [
    existingAssessment,
    allAnswerableRefs,
    assessmentLoading,
    assessmentFetching,
    assessmentStarted,
  ]);

  // ── Handlers ────────────────────────────────────────────────────────

  const handleChange = (
    questionRef: string,
    field: "answer" | "details",
    value: string,
  ) => {
    setAnswers((prev) => {
      const current = prev[questionRef] ?? { answer: "", details: "" };
      return {
        ...prev,
        [questionRef]: { ...current, [field]: value } as AnswerState,
      };
    });
  };

  const buildPayload = (status: "draft" | "submitted") => {
    const payloadAnswers: CRAComplianceAnswer[] = allAnswerableRefs.map(
      (ref) => ({
        questionRef: ref,
        answer: answers[ref]?.answer || "",
        details: answers[ref]?.details || "",
      }),
    );
    return { version: "1.0", status, answers: payloadAnswers };
  };

  const handleSave = () => {
    saveMutation.mutate(buildPayload("draft"));
  };

  const handleSubmit = () => {
    const unanswered = allAnswerableRefs.filter((ref) => !answers[ref]?.answer);
    if (unanswered.length > 0) {
      toast.error(
        `Please answer all questions. ${unanswered.length} remaining.`,
      );
      return;
    }

    // First save, then submit
    const payload = buildPayload("draft");
    const saveFirst = existingAssessmentId
      ? apiService.updateCRAComplianceAssessment(existingAssessmentId, payload)
      : apiService.createCRAComplianceAssessment(id!, payload);

    saveFirst
      .then((response: any) => {
        const assessId = response?.data?.data?.id || existingAssessmentId;
        if (assessId) {
          setExistingAssessmentId(assessId);
          submitMutation.mutate(assessId);
        } else {
          toast.error("Could not determine assessment ID");
        }
      })
      .catch((err: any) => {
        toast.error(
          err.response?.data?.error || "Failed to save before submit",
        );
      });
  };

  // ── Loading / Error States ──────────────────────────────────────────

  if (productLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Product not found</p>
      </div>
    );
  }

  // ── Feedback Screen ─────────────────────────────────────────────────

  if (showFeedback) {
    const feedbackAnswers: CRAComplianceAnswer[] = allAnswerableRefs.map(
      (ref) => ({
        questionRef: ref,
        answer: answers[ref]?.answer || "",
        details: answers[ref]?.details || "",
      }),
    );

    return (
      <div className="max-w-full -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-3 flex flex-col h-[calc(100vh-7rem)] lg:h-[calc(100vh-3rem)] overflow-hidden">
        <div className="flex items-center space-x-3 flex-shrink-0">
          <button
            onClick={() => navigate(`/products/${id}`)}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
              CRA Compliance Results
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {product.name}
            </p>
          </div>
        </div>

        <CRAComplianceFeedback
          productId={id!}
          productName={product.name}
          answers={feedbackAnswers}
          questions={apiQuestions}
        />
      </div>
    );
  }

  // ── Progress Counters ───────────────────────────────────────────────

  const answeredCount = allAnswerableRefs.filter(
    (ref) => !!answers[ref]?.answer,
  ).length;

  if (apiQuestions.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">No CRA Compliance questions available</p>
      </div>
    );
  }

  // ── Main Render ─────────────────────────────────────────────────────

  return (
    <div className="max-w-full -mx-4 sm:-mx-6 lg:-mx-8 -my-6 px-4 sm:px-6 lg:px-8 py-3 flex flex-col h-[calc(100dvh-7rem)] lg:h-[calc(100dvh-3rem)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center space-x-3 flex-shrink-0">
        <button
          onClick={() => navigate(`/products/${id}`)}
          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
            CRA Compliance assessment
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {product.name} &mdash; EU Cyber Resilience Act
          </p>
        </div>
      </div>

      {/* Progress Bar — hidden on the initial landing screen to save space */}
      {(existingAssessment || assessmentStarted) && (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2 flex-shrink-0 mt-2">
          {isViewOnly && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mb-1">
              <Lock className="h-3 w-3" />
              <span>This assessment has been submitted and is read-only</span>
            </div>
          )}
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              CRA Compliance &mdash; v1.0
              {existingAssessment?.updatedAt && (
                <span className="ml-2 text-gray-400 dark:text-gray-500">
                  &middot; Last updated{" "}
                  {new Date(existingAssessment.updatedAt).toLocaleDateString(
                    "en-GB",
                    {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    },
                  )}
                </span>
              )}
            </span>
            <span>
              {answeredCount}/{allAnswerableRefs.length} answered
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 mt-1.5">
            <div
              className="bg-green-600 h-1.5 rounded-full transition-all"
              style={{
                width: `${allAnswerableRefs.length ? (answeredCount / allAnswerableRefs.length) * 100 : 0}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      {assessmentLoading ? (
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" />
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Loading assessment...
            </p>
          </div>
        </div>
      ) : existingAssessment?.status === "submitted" && !assessmentStarted ? (
        /* Submitted – show view-only landing */
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center max-w-md px-6">
            <div className="w-16 h-16 rounded-full bg-green-50 dark:bg-green-900/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Assessment Submitted
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              The CRA Compliance assessment for this product has been submitted.
              You can view the responses or retake the assessment.
            </p>
            {existingAssessment?.updatedAt && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
                Last updated
                {existingAssessment.lastUpdatedByName
                  ? ` by ${existingAssessment.lastUpdatedByName}`
                  : ""}{" "}
                on{" "}
                {new Date(existingAssessment.updatedAt).toLocaleDateString(
                  "en-GB",
                  {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  },
                )}
              </p>
            )}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/products/${id}`)}
                className="btn btn-outline text-sm w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Product
              </button>
              <button
                onClick={() => setAssessmentStarted(true)}
                className="btn btn-outline text-sm w-full sm:w-auto"
              >
                <Eye className="h-4 w-4 mr-1" />
                View Responses
              </button>
              <button
                onClick={() => setShowFeedback(true)}
                className="btn btn-outline text-sm w-full sm:w-auto border-blue-200 text-blue-700 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400 dark:hover:bg-blue-900/20"
              >
                <BarChart3 className="h-4 w-4 mr-1" />
                View Feedback
              </button>
              {(product.ownerId === user?.id || isAdmin(user)) && (
                <button
                  onClick={() => {
                    if (existingAssessmentId) {
                      retakeMutation.mutate(existingAssessmentId);
                    }
                  }}
                  disabled={retakeMutation.isLoading}
                  className="btn btn-primary text-sm w-full sm:w-auto"
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-1 ${retakeMutation.isLoading ? "animate-spin" : ""}`}
                  />
                  {retakeMutation.isLoading
                    ? "Reopening..."
                    : "Retake Assessment"}
                </button>
              )}
            </div>
          </div>
        </div>
      ) : existingAssessment?.status === "draft" && !assessmentStarted ? (
        /* Draft exists – continue screen */
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex items-center justify-center">
          <div className="text-center max-w-lg px-6">
            <div className="w-20 h-20 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mx-auto mb-5">
              <ClipboardList className="h-10 w-10 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Continue CRA Compliance assessment
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6 leading-relaxed">
              You have a draft assessment in progress. Your previously saved
              answers will be loaded when you continue.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <button
                onClick={() => navigate(`/products/${id}`)}
                className="btn btn-outline text-sm w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back to Product
              </button>
              <button
                onClick={() => setAssessmentStarted(true)}
                className="btn btn-primary text-sm w-full sm:w-auto"
              >
                <PlayCircle className="h-4 w-4 mr-1" />
                Continue Assessment
              </button>
            </div>
          </div>
        </div>
      ) : !existingAssessment && !assessmentStarted ? (
        /* No assessment – start fresh */
        <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex flex-col min-h-0">
          {/* Content area */}
          {/* Content area — scrolls when its content exceeds the viewport
              so the hero header above and the sticky footer below stay
              visible. */}
          <div
            ref={landingScrollRef}
            className="flex-1 min-h-0 overflow-y-auto px-4 py-2.5 pb-4 sm:px-6 sm:py-3 sm:pb-5 flex flex-col gap-2.5"
          >
            {/* Hero header — compact */}
            <div className="relative rounded-lg bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 dark:from-blue-900/20 dark:via-indigo-900/15 dark:to-purple-900/10 border border-blue-100 dark:border-blue-800/30 px-3 py-2.5 sm:px-5 sm:py-4">
              <div className="relative flex items-start sm:items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-12 sm:h-12 rounded-xl bg-white dark:bg-gray-800 shadow-sm border border-blue-200 dark:border-blue-700/50 flex items-center justify-center flex-shrink-0">
                  <ClipboardList className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-xl font-bold text-gray-900 dark:text-gray-100 leading-tight truncate">
                    CRA Compliance assessment
                  </h3>
                  <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap mt-0.5 text-[10px] sm:text-sm text-gray-600 dark:text-gray-400">
                    <span>🇪🇺 EU Cyber Resilience Act</span>
                    <span className="hidden sm:inline text-gray-300 dark:text-gray-600">
                      ·
                    </span>
                    <span>📝 {apiQuestions.length} questions</span>
                    <span className="hidden sm:inline text-gray-300 dark:text-gray-600">
                      ·
                    </span>
                    <span className="text-blue-700 dark:text-blue-300 font-medium">
                      ✅ Draft &amp; submit supported
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Info cards */}
            {(() => {
              const cards = [
                {
                  emoji: "📋",
                  title: "About",
                  text: "This survey helps product owners evaluate compliance with the EU Cyber Resilience Act. It includes 20 questions, some with sub-questions. Save your progress as a draft, submit when finished, and update your answers anytime.",
                  border: "border-blue-100 dark:border-blue-800/40",
                  bg: "from-blue-50/80 to-white dark:from-blue-900/15 dark:to-gray-900",
                  iconBg: "bg-blue-100 dark:bg-blue-900/40",
                },
                {
                  emoji: "🎯",
                  title: "Purpose",
                  text: "The assessment provides an initial evaluation of compliance status from perspective of manufacturer obligations concerning a product in CRA category default (not for critical or important products). Achieving CRA compliance always requires a formal conformity assessment, as set out in CRA Article 32. This assessment is intended for teams and divisions/business units to strategically plan their roadmap towards CRA compliance. This assessment does not constitute a CRA compliant conformity assessment.",
                  border: "border-green-100 dark:border-green-800/40",
                  bg: "from-green-50/80 to-white dark:from-green-900/15 dark:to-gray-900",
                  iconBg: "bg-green-100 dark:bg-green-900/40",
                },
                {
                  emoji: "⚠️",
                  title: "Evidence",
                  text: "Each question includes sample evidence to guide answers by illustrating typical documentation and outcomes. Use an issue or requirement management tool to document traceability between requirements, implementation, testing, and evidence.",
                  border: "border-amber-200 dark:border-amber-700/40",
                  bg: "from-amber-50/80 to-white dark:from-amber-900/15 dark:to-gray-900",
                  iconBg: "bg-amber-100 dark:bg-amber-900/40",
                },
              ];
              return (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {cards.map((c) => (
                    <div
                      key={c.title}
                      className={`rounded-lg border ${c.border} bg-gradient-to-b ${c.bg} px-3.5 py-3 sm:p-4 flex flex-col`}
                    >
                      <div className="flex items-center gap-2 mb-1.5">
                        <div
                          className={`w-7 h-7 sm:w-8 sm:h-8 rounded-md flex items-center justify-center flex-shrink-0 shadow-sm ${c.iconBg}`}
                        >
                          <span className="text-sm sm:text-base">
                            {c.emoji}
                          </span>
                        </div>
                        <h4 className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100">
                          {c.title}
                        </h4>
                      </div>
                      <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 leading-relaxed flex-1">
                        {c.text}
                      </p>
                    </div>
                  ))}
                </div>
              );
            })()}

            {/* CRA Requirement library banner */}
            <div className="rounded-lg border border-indigo-200 dark:border-indigo-800/40 bg-gradient-to-r from-indigo-50/80 to-white dark:from-indigo-900/15 dark:to-gray-900 px-3.5 py-3 sm:p-4">
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-md bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0 shadow-sm">
                  <Library className="h-4 w-4 sm:h-5 sm:w-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100 mb-1">
                    CRA requirement library
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                    The{" "}
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await apiService.downloadCRARequirementLibraryCSV();
                        } catch (err) {
                          toast.error(
                            extractApiError(
                              err,
                              "Failed to download CRA Requirement library",
                            ),
                          );
                        }
                      }}
                      className="text-indigo-600 dark:text-indigo-400 font-medium underline hover:text-indigo-700 dark:hover:text-indigo-300 inline-flex items-center gap-0.5"
                    >
                      CRA Requirement library
                      <Download className="h-3 w-3" />
                    </button>{" "}
                    is provided in CSV format for purpose of importing more
                    detailed requirements to an issue or requirement management
                    tool. Each requirement library&rsquo;s requirement is
                    connected to a CRA compliance assessment question,
                    referencing its designated requirement ID. Note that
                    answering &ldquo;yes&rdquo; in some questions may require
                    implementing several requirements.
                  </p>
                </div>
              </div>
            </div>

            {/* CRA class gating */}
            <CRAClassGate
              craClassification={product.craClassification}
              craApplicability={product.craApplicability}
            />

            {/* Action buttons — inline, scrollable with the rest of the content */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-2 sm:gap-3 pt-2 pb-1">
              <button
                onClick={() => navigate(`/products/${id}`)}
                className="btn btn-outline text-sm px-5 py-2 w-full sm:w-auto"
              >
                <ArrowLeft className="h-4 w-4 mr-1.5" />
                Back to Product
              </button>
              <button
                onClick={() => setAssessmentStarted(true)}
                disabled={!craClass.determined}
                title={
                  !craClass.determined
                    ? "CRA class must be determined before starting the assessment"
                    : undefined
                }
                className="btn btn-primary text-sm px-5 py-2 w-full sm:w-auto shadow-md shadow-blue-200 dark:shadow-blue-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlayCircle className="h-4 w-4 mr-1.5" />
                Begin Assessment
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* ─── Active Assessment (started / view-only) ─── */
        <>
          {/* Single question per page */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 mt-2 flex-1 flex flex-col min-h-0">
            <div className="flex-1 overflow-y-auto min-h-0 px-4 py-3">
              {/* Q1 conditional guidance — driven by economic operators + Q1 answer */}
              {apiQuestions[currentStep]?.ref === "1" && (
                <Q1RoleGuidance
                  productId={id!}
                  answer={answers["1"]?.answer}
                  operators={economicOperators}
                  canEdit={canEdit}
                />
              )}
              {apiQuestions[currentStep] && (
                <CRAComplianceQuestionCard
                  key={apiQuestions[currentStep].ref}
                  index={currentStep}
                  question={apiQuestions[currentStep]}
                  answers={answers}
                  onChange={handleChange}
                  disabled={!canEdit}
                />
              )}
            </div>
          </div>

          {/* Dot Navigation */}
          <div className="flex items-center justify-center gap-1.5 mt-2 flex-shrink-0">
            {apiQuestions.map((q, i) => {
              const refs =
                q.subQuestions.length > 0
                  ? q.subQuestions.map((sq) => sq.ref)
                  : [q.ref];
              const allAnswered = refs.every((r) => !!answers[r]?.answer);
              const allNA = refs.every(
                (r) => answers[r]?.answer === "Not applicable",
              );
              const isCurrent = i === currentStep;
              return (
                <button
                  key={q.ref}
                  onClick={() => setCurrentStep(i)}
                  className={`w-7 h-7 rounded-full text-xs font-medium transition-all flex items-center justify-center ${
                    isCurrent
                      ? "bg-blue-600 text-white scale-110 shadow"
                      : allNA
                        ? "bg-gray-200 dark:bg-gray-700 text-gray-400"
                        : allAnswered
                          ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                          : "bg-gray-100 dark:bg-gray-800 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                  title={`Q${i + 1}: ${q.name}`}
                >
                  {allNA ? "—" : allAnswered ? "✓" : i + 1}
                </button>
              );
            })}
          </div>

          {/* Footer: Prev / Save / Submit / Next */}
          <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center justify-between flex-shrink-0 mt-2">
            <button
              onClick={() => setCurrentStep((s) => Math.max(0, s - 1))}
              disabled={currentStep === 0}
              className="btn btn-outline text-sm"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>

            <div className="flex items-center gap-2">
              {isViewOnly ? (
                <>
                  <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <span>Submitted</span>
                  </div>
                  <button
                    onClick={() => navigate(`/products/${id}`)}
                    className="btn btn-outline text-sm"
                  >
                    <ArrowLeft className="h-4 w-4 mr-1" />
                    Back
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleSave}
                    disabled={
                      saveMutation.isLoading || submitMutation.isLoading
                    }
                    className="btn btn-outline text-sm"
                  >
                    <Save className="h-4 w-4 mr-1" />
                    {saveMutation.isLoading ? "Saving..." : "Save Draft"}
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={
                      saveMutation.isLoading || submitMutation.isLoading
                    }
                    className="btn btn-primary text-sm"
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    {submitMutation.isLoading ? "Submitting..." : "Submit"}
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() =>
                setCurrentStep((s) => Math.min(apiQuestions.length - 1, s + 1))
              }
              disabled={currentStep === apiQuestions.length - 1}
              className="btn btn-outline text-sm"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper components
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derives the effective CRA class for a product from either the manually-set
 * `craClassification` field (Default | Important | Critical I | Critical II) or,
 * when that is empty, the result of the CRA Applicability Assessment
 * (`craApplicability`, e.g. "cra_important_1"). The manual classification takes
 * precedence.
 *
 * Returns:
 *  - determined: whether a CRA class has been established at all
 *  - label:      human-readable class label (null when not determined)
 *  - isElevated: true for Important/Critical classes (warning shown, but allowed)
 */
function deriveCraClass(
  craClassification?: string | null,
  craApplicability?: string | null,
): { determined: boolean; label: string | null; isElevated: boolean } {
  // Manually-set classification takes precedence.
  if (craClassification) {
    return {
      determined: true,
      label: craClassification,
      isElevated: craClassification !== "Default",
    };
  }

  // Otherwise derive from the CRA Applicability Assessment result.
  switch (craApplicability) {
    case "cra_default":
      return { determined: true, label: "Default", isElevated: false };
    case "cra_important_1":
      return { determined: true, label: "Important Class I", isElevated: true };
    case "cra_important_2":
      return {
        determined: true,
        label: "Important Class II",
        isElevated: true,
      };
    case "cra_critical":
      return { determined: true, label: "Critical", isElevated: true };
    case "cra_partially":
      return {
        determined: true,
        label: "Partially applicable",
        isElevated: true,
      };
    case "not_in_scope":
      return { determined: true, label: "Out of scope", isElevated: false };
    default:
      return { determined: false, label: null, isElevated: false };
  }
}

/**
 * Gating block shown on the landing page based on the product's CRA class.
 * The class is taken from the manual `craClassification` field or, as a
 * fallback, the CRA Applicability Assessment result (`craApplicability`).
 * - not determined → block "Begin Assessment" + CTA to CRA Applicability Tool
 * - Default / out of scope → allow (no warning rendered)
 * - Important / Critical   → allow + warning about critical/important products
 */
function CRAClassGate({
  craClassification,
  craApplicability,
}: {
  craClassification?: string | null;
  craApplicability?: string | null;
}) {
  const { determined, label, isElevated } = deriveCraClass(
    craClassification,
    craApplicability,
  );

  if (!determined) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800/40 bg-red-50/60 dark:bg-red-900/15 p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800 dark:text-red-200 leading-relaxed">
            <p className="font-semibold mb-1">CRA class not determined</p>
            <p>
              Please start by determining the CRA class for the product. If not
              known, the product&rsquo;s CRA class can be assessed with the{" "}
              <a
                href="#"
                target="_blank"
                rel="noopener noreferrer"
                className="underline font-medium hover:text-red-900 dark:hover:text-red-100 inline-flex items-center gap-0.5"
              >
                CRA Applicability Tool
                <ExternalLink className="h-3 w-3" />
              </a>
              . The CRA Compliance assessment cannot be started until a CRA
              class is set on the product.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!isElevated) {
    return null;
  }

  // Important / Critical I / Critical II — warn but allow.
  return (
    <div className="rounded-lg border border-amber-200 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-900/15 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 dark:text-amber-100 leading-relaxed">
          <p className="font-semibold mb-1">Product CRA class: {label}</p>
          <p className="italic">
            The CRA Compliance Assessment is designed for products that are{" "}
            <strong>not</strong> in critical or important categories. Please
            note that products in these categories have specific requirements
            that are not addressed in the CRA Compliance assessment and seek
            further advice if needed.
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * Conditional guidance rendered above Question 1 ("Role as economic operator")
 * based on the Q1 Yes/No answer and the configured economic operators.
 */
function Q1RoleGuidance({
  productId,
  answer,
  operators,
  canEdit,
}: {
  productId: string;
  answer: string | undefined;
  operators: CRAEconomicOperator[];
  canEdit: boolean;
}) {
  if (!answer) return null;

  // Answer = NO → recommend determining CRA role in advance.
  if (answer === "No") {
    return (
      <div className="mb-3 rounded-md border border-amber-200 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-900/15 p-3 text-sm leading-relaxed text-amber-900 dark:text-amber-100 italic">
        Note that this CRA Compliance assessment is designed only for Sandvik
        entities operating as CRA manufacturers. It is therefore recommended
        that the CRA role is determined in advance.
      </div>
    );
  }

  // Answer = YES → always surface the CRA Role Information card so the user can
  // view, add, edit or remove the legal entity/role at any time — not only when
  // the list is empty. (Previously the card was hidden once an operator had been
  // added, leaving no way to modify the entered role.) Contextual guidance is
  // shown above the card based on the configured roles.
  const roles = Array.from(new Set(operators.map((o) => o.role)));
  const hasImporterOrDistributor = roles.some(
    (r) => r === "importer" || r === "distributor",
  );

  return (
    <div className="mb-3 space-y-2">
      {operators.length === 0 && (
        <div className="rounded-md border border-blue-200 dark:border-blue-700/40 bg-blue-50/60 dark:bg-blue-900/15 p-3 text-sm leading-relaxed text-blue-900 dark:text-blue-100">
          You answered <strong>Yes</strong> but no CRA role information has been
          added yet. Please add the legal entity and its role below.
        </div>
      )}

      {hasImporterOrDistributor && (
        <div className="rounded-md border border-amber-200 dark:border-amber-700/40 bg-amber-50/60 dark:bg-amber-900/15 p-3 text-sm leading-relaxed text-amber-900 dark:text-amber-100 space-y-2">
          <p className="italic">
            Note that this CRA Compliance assessment is designed only for
            Sandvik entities operating as CRA manufacturers.
          </p>
          <p className="italic">
            CRA importers and distributors have certain specific obligations
            under the CRA. See{" "}
            <a
              href="#"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium inline-flex items-center gap-0.5"
            >
              CRA Instruction
              <ExternalLink className="h-3 w-3" />
            </a>{" "}
            for guidance.
          </p>
        </div>
      )}

      <EconomicOperatorsCard
        productId={productId}
        compact
        readOnly={!canEdit}
      />
    </div>
  );
}
